import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Space, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  loadNodeEndpoints,
  probeNodeEndpoint,
  type NodeProbeState,
  type NodeStatus,
} from '../../services/nodeStatus';
import './NodeStatusModal.css';

export { DEFAULT_NODE_ENDPOINTS } from '../../services/nodeStatus';
export { loadNodeEndpoints, probeNodeEndpoint } from '../../services/nodeStatus';
export type { NodeProbeState, NodeStatus } from '../../services/nodeStatus';

interface NodeStatusModalProps {
  open: boolean;
  onClose: () => void;
}

const formatEndpointDomain = (endpoint: string): string => {
  try {
    return new URL(endpoint).hostname;
  } catch {
    return endpoint;
  }
};

const NodeStatusModal: React.FC<NodeStatusModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile(768);
  const [nodes, setNodes] = useState<NodeStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const runIdRef = useRef(0);

  const probeNodes = useCallback(async () => {
    const runId = ++runIdRef.current;
    setLoading(true);
    setNodes([]);
    const endpoints = await loadNodeEndpoints();
    if (runId !== runIdRef.current) return;
    setNodes(endpoints.map((endpoint) => ({ endpoint, state: 'pending', latencyMs: null })));
    const results = await Promise.all(endpoints.map(probeNodeEndpoint));
    if (runId === runIdRef.current) {
      setNodes(results);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void probeNodes();
  }, [open, probeNodes]);

  const columns: TableProps<NodeStatus>['columns'] = [
    {
      title: t('nodeStatus.endpoint'),
      dataIndex: 'endpoint',
      key: 'endpoint',
      ellipsis: isMobile,
      render: (endpoint: string) => (
        <Typography.Text
          className="node-status-endpoint"
          copyable={{ text: endpoint }}
          ellipsis={isMobile ? { tooltip: formatEndpointDomain(endpoint) } : false}
        >
          {formatEndpointDomain(endpoint)}
        </Typography.Text>
      ),
    },
    {
      title: t('nodeStatus.status'),
      dataIndex: 'state',
      key: 'state',
      width: 110,
      render: (state: NodeProbeState) => {
        if (state === 'pending') return <Tag>{t('nodeStatus.probing')}</Tag>;
        return <Tag color={state === 'success' ? 'success' : 'error'}>{t(`nodeStatus.${state}`)}</Tag>;
      },
    },
    {
      title: t('nodeStatus.latency'),
      dataIndex: 'latencyMs',
      key: 'latencyMs',
      width: isMobile ? 68 : 110,
      align: isMobile ? 'right' : undefined,
      render: (latencyMs: number | null) => (
        <span className="node-status-latency">{latencyMs === null ? '-' : `${latencyMs} ms`}</span>
      ),
    },
    {
      title: t('nodeStatus.action'),
      key: 'action',
      width: isMobile ? 56 : 100,
      render: (_value: unknown, record: NodeStatus) => (
        <Button
          className="node-status-visit"
          type="link"
          size={isMobile ? 'small' : 'middle'}
          icon={isMobile ? undefined : <LinkOutlined />}
          href={record.endpoint}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('nodeStatus.visit')}
        </Button>
      ),
    },
  ];

  return (
    <Modal
      className="node-status-modal"
      title={t('nodeStatus.title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={680}
      footer={[
        <Button key="reload" icon={<ReloadOutlined />} loading={loading} onClick={() => void probeNodes()}>
          {t('nodeStatus.reprobe')}
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>{t('common.close')}</Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Table<NodeStatus>
          rowKey="endpoint"
          size="small"
          loading={loading && nodes.length === 0}
          columns={isMobile ? columns.filter((column) => column.key !== 'state') : columns}
          dataSource={nodes}
          pagination={false}
        />
      </Space>
    </Modal>
  );
};

export default NodeStatusModal;
